const enum LoggerLevelType {
  DEBUG,
  INFO,
  WARN,
  ERROR,
}

const loggerPrefix = {
  [LoggerLevelType.DEBUG]: '[DEBUG]',
  [LoggerLevelType.INFO]: '[INFO]',
  [LoggerLevelType.WARN]: '[WARN]',
  [LoggerLevelType.ERROR]: '[ERROR]',
}

const loggerStyle = {
  [LoggerLevelType.DEBUG]: 'color: lightgreen;',
  [LoggerLevelType.INFO]: 'color: blue;',
  [LoggerLevelType.WARN]: 'color: blue;',
  [LoggerLevelType.ERROR]: 'color: red;',
}

type ProcessLogType = Parameters<
  (timestamp: number, type: LoggerLevelType, message?: unknown, ...optionalParams: unknown[]) => void
>

/** 最大日志数量 */
const MAX_LOG_COUNT = 10000

class Logger {
  logs: ProcessLogType[] = []

  private lastLevel: undefined | LoggerLevelType = undefined

  private level = LoggerLevelType.INFO

  setLevel = (type: LoggerLevelType) => {
    this.lastLevel = this.level
    this.level = type

    if (this.lastLevel === this.level) return

    this.logs.forEach(([logType, message, ...optionalParams]) => {
      this.processLog(logType, message, ...optionalParams)
    })
  }

  usePrefix = (s: string) => ({
    debug: this.debug.bind(this, s),
    info: this.info.bind(this, s),
    warn: this.warn.bind(this, s),
    error: this.error.bind(this, s),
  })

  debug = (message?: unknown, ...optionalParams: unknown[]) => {
    const time = Date.now()
    this.setLog([time, LoggerLevelType.DEBUG, message, ...optionalParams])
    this.processLog(time, LoggerLevelType.DEBUG, message, ...optionalParams)
  }

  info = (message?: unknown, ...optionalParams: unknown[]) => {
    const time = Date.now()
    this.setLog([time, LoggerLevelType.INFO, message, ...optionalParams])
    this.processLog(time, LoggerLevelType.INFO, message, ...optionalParams)
  }

  warn = (message?: unknown, ...optionalParams: unknown[]) => {
    const time = Date.now()
    this.setLog([time, LoggerLevelType.WARN, message, ...optionalParams])
    this.processLog(time, LoggerLevelType.WARN, message, ...optionalParams)
  }

  error = (message?: unknown, ...optionalParams: unknown[]) => {
    const time = Date.now()
    this.setLog([time, LoggerLevelType.ERROR, message, ...optionalParams])
    this.processLog(time, LoggerLevelType.ERROR, message, ...optionalParams)
  }

  clearLogs = () => {
    this.logs = []
  }

  private processLog = (timestamp: number, type: LoggerLevelType, message?: unknown, ...optionalParams: unknown[]) => {
    if (this.level > type) {
      return
    }

    const timeString = new Date(timestamp).toLocaleString()

    const msg = [
      `%c[${timeString}] %c${loggerPrefix[type]}`,
      'color: blue;',
      loggerStyle[type],
      message,
      ...optionalParams,
    ]

    if (type === LoggerLevelType.DEBUG || type === LoggerLevelType.INFO) {
      console.log(...msg)
    }

    if (type === LoggerLevelType.WARN) {
      console.warn(...msg)
    }

    if (type === LoggerLevelType.ERROR) {
      console.error(...msg)
    }
  }

  private setLog = (params: ProcessLogType) => {
    // 达到记录上限时, 删除最前面的
    if (this.logs.length >= MAX_LOG_COUNT) {
      this.logs.splice(0, 1)
    }

    this.logs.push(params)
  }
}

const logger = new Logger()
window._ppLogger = logger

export type { Logger }
export default logger
